import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useOrg } from '../../../shared/contexts/OrgContext';
import AppLayout from '../../../shared/components/layout/AppLayout';
import AppCard from '../../../shared/components/ui/AppCard';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { useAppAccess } from '../../../shared/hooks/useAppAccess';
import { supabase } from '../../../shared/lib/supabase';

interface AppItem {
  id: string;
  name: string;
  icon: string;
  status?: 'TRY' | 'NEW' | 'DISABLED' | null;
  disabled?: boolean;
}

const HomePage: React.FC = () => {
  const [showAllApps, setShowAllApps] = useState(false);
  const [realUserData, setRealUserData] = useState<any>(null);
  const { user } = useAuth();
  const { currentOrganization } = useOrg();
  const { hasAccess, loading: accessLoading } = useAppAccess();

  const location = useLocation();
  const navigate = useNavigate();
  
  // Get organization ID from URL path (proper UUID extraction)
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const orgId = orgIdFromPath || 'default';

  // Helper function to get display name from user data
  const getDisplayName = (): string => {
    if (realUserData?.first_name && realUserData?.last_name) {
      return `${realUserData.first_name} ${realUserData.last_name}`;
    }
    if (realUserData?.first_name) {
      return realUserData.first_name;
    }
    if (user?.email) {
      const username = user.email.split('@')[0];
      return username.charAt(0).toUpperCase() + username.slice(1);
    }
    return 'User';
  };

  // Helper function to get user initials from user data
  const getUserInitials = (): string => {
    if (realUserData?.first_name && realUserData?.last_name) {
      return `${realUserData.first_name[0]}${realUserData.last_name[0]}`.toUpperCase();
    }
    if (realUserData?.first_name) {
      return realUserData.first_name[0]?.toUpperCase() || 'U';
    }
    if (user?.email) {
      const parts = user.email.split('@')[0].split('.');
      return parts.map(part => part[0]?.toUpperCase() || '').join('').slice(0, 2) || 'U';
    }
    return 'U';
  };

  // Get current date
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // All apps data - Only apps that exist in the system
  const allApps: AppItem[] = [
    {
      id: 'replay',
      name: 'Replay',
      icon: '🔄'
    },
    {
      id: 'reports',
      name: 'Reports',
      icon: '📊'
    },
    {
      id: 'score',
      name: 'Score',
      icon: '⚽'
    },
    {
      id: 'capture',
      name: 'Capture',
      icon: '📷',
      status: 'NEW'
    },
    {
      id: 'cms',
      name: 'CMS',
      icon: '📝'
    },
    {
      id: 'control-room',
      name: 'Control Room',
      icon: '🎮'
    },
    {
      id: 'roster',
      name: 'Roster',
      icon: '👥'
    }
  ];

  // Fetch real user data from database
  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.email) {
        try {
          const { data: userData, error } = await supabase
            .from('users')
            .select('*')
            .eq('email', user.email)
            .eq('active', true)
            .single();

          if (!error && userData) {
            setRealUserData(userData);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
        } 
      }
    };

    fetchUserData();
  }, [user?.email]);

  // Load organization when orgId changes
  useEffect(() => {
    if (orgId) {
      // The OrgContext will handle loading the organization
    }
  }, [orgId]);

  // Handle organization loading and URL correction
  useEffect(() => {
    const handleOrganizationLoading = async () => {
      // If URL is /o/default, redirect to organization selection
      if (orgId === 'default') {
        navigate('/select-organization', { replace: true });
        return;
      }
      
      // Don't auto-redirect to current organization - respect user's URL choice
      // The OrgContext will handle loading the correct organization data
    };

    handleOrganizationLoading();
  }, [orgId, user, currentOrganization, navigate]);

  // Filter apps based on access - only show apps user has access to
  const accessibleApps = allApps.filter(app => hasAccess(app.id));
  
  // Calculate how many apps fit in one line (6 apps per row on desktop)
  const appsPerLine = 6;
  const showMoreButton = accessibleApps.length > appsPerLine;

  // Handle app navigation - All apps now use organization-based routes
  const handleAppClick = (appId: string) => {
    // Get the actual organization ID from current organization or user
    const actualOrgId = currentOrganization?.id || orgId;
    
    // All apps now navigate to organization-specific routes
    if (appId === 'score') {
      navigate(`/o/${actualOrgId}/score`);
    } else if (appId === 'capture') {
      navigate(`/o/${actualOrgId}/capture`);
    } else if (appId === 'replay') {
      navigate(`/o/${actualOrgId}/replay`);
    } else if (appId === 'reports') {
      navigate(`/o/${actualOrgId}/reports`);
    } else if (appId === 'cms') {
      navigate(`/o/${actualOrgId}/cms`);
    } else if (appId === 'control-room') {
      // Control Room app - Navigate to organization-specific route
      navigate(`/o/${actualOrgId}/control-room`);
    } else if (appId === 'roster') {
      // Roster app - Navigate to organization-specific route
      navigate(`/o/${actualOrgId}/roster`);
    }
  };



  return (
    <AppLayout currentApp="Home" orgId={orgId} showNotifications={true} showDiscover={true}>
      <div className="p-3 sm:p-6">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 text-white relative overflow-hidden">
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-start sm:justify-between">
            {/* Left Side - User Info */}
            <div className="flex-1">
              <p className="text-xs sm:text-sm opacity-90 mb-2">{currentDate}</p>
              <h1 className="text-lg sm:text-2xl font-bold">
                Hello, {getDisplayName()}
              </h1>
              {currentOrganization && (
                <p className="text-sm sm:text-base opacity-90 mt-2">
                  {currentOrganization.name}
                </p>
              )}
            </div>

            {/* Right Side - Additional Info */}
            <div className="flex flex-col sm:items-end sm:text-right mt-4 sm:mt-0">
              {user?.email && (
                <p className="text-xs sm:text-sm opacity-75 mb-2">
                  {user.email}
                </p>
              )}
              {realUserData?.created_at && (
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-3 py-2 text-xs sm:text-sm">
                  Member since {new Date(realUserData.created_at).getFullYear()}
                </div>
              )}
            </div>
          </div>
          {/* Decorative lamp */}
          <div className="absolute right-4 sm:right-8 top-4 text-3xl sm:text-5xl opacity-30">💡</div>
        </div>

        {/* Your Apps Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Your apps</h2>
            {showMoreButton && (
              <button
                onClick={() => setShowAllApps(!showAllApps)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1 transition-colors min-h-44 min-w-44"
              >
                <span>{showAllApps ? 'Show less' : 'Show all'}</span>
                <svg className={`w-4 h-4 transform transition-transform ${showAllApps ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
          </div>

          {/* Apps Grid */}
          {accessLoading ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Loading apps...</div>
            </div>
          ) : (
            <>
              {/* Show accessible apps */}
              {accessibleApps.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
                  {/* First row - Always show first 6 apps */}
                          {accessibleApps.slice(0, appsPerLine).map((app) => (
                            <AppCard 
                              key={app.id} 
                              id={app.id}
                              name={app.name}
                              icon={app.icon}
                              status={app.status}
                              disabled={app.disabled || false}
                              onClick={() => handleAppClick(app.id)}
                              className=""
                            />
                          ))}
                </div>
              )}
              
              {/* Additional apps - Show when "Show all" is clicked */}
              {showAllApps && accessibleApps.length > appsPerLine && (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mt-4">
                          {accessibleApps.slice(appsPerLine).map((app) => (
                            <AppCard 
                              key={app.id} 
                              id={app.id}
                              name={app.name}
                              icon={app.icon}
                              status={app.status}
                              disabled={app.disabled || false}
                              onClick={() => handleAppClick(app.id)}
                              className=""
                            />
                          ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Frequent Places Section */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Frequent places</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-gray-600 text-xs sm:text-sm">
                    No frequent places yet. Start using the apps to see your most visited areas here.
                  </p>
                  <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500 mt-1">
                    <span>📍</span>
                    <span>Home</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What's Next Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">What's next</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">You're all done for now. Check back soon to find out what's next.</p>
          <div className="flex flex-wrap gap-3 sm:gap-4 text-sm">
            <a href="#" className="text-blue-600 hover:text-blue-700 underline min-h-44">Worked on</a>
            <a href="#" className="text-blue-600 hover:text-blue-700 underline min-h-44">Viewed</a>
            <a href="#" className="text-blue-600 hover:text-blue-700 underline font-medium min-h-44">Recommended</a>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-6 sm:mt-8 bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Recent activity</h2>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-medium text-xs sm:text-sm">
                {getUserInitials()}
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="text-sm sm:text-base font-medium text-gray-900">{getDisplayName()}</span>
                  <span className="text-gray-500">•</span>
                  <span className="text-xs sm:text-sm text-gray-500">Just now</span>
                </div>
                <p className="text-xs sm:text-sm text-gray-600">No recent activity to show. Start using the apps to see your latest actions here.</p>
                <div className="text-xs sm:text-sm text-gray-600">
                  Member since {realUserData?.created_at ? new Date(realUserData.created_at).getFullYear() : '2025'} • <span className="text-blue-600">Home</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default HomePage;